import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AgentVerificationStatus } from '@percent/admin-dashboard/api/types'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { useMutation, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { Alert, ButtonText, CardContent, CardDashboard, Spacer } from '@percent/lemonade'
import styles from './AgentDetails.module.scss'
import { AgentDetailsProps } from './AgentDetails.types'

export function AgentDetails({
  agentVerification,
  agentVerificationStatus,
  isAgentEmailAutoApproved,
  isOrgDataAvailable,
  hasInboxVerificationEmailSentLog
}: Readonly<AgentDetailsProps>) {
  const { t } = useTranslation()
  const { agentVerificationService } = useServices()
  const { id } = useRequiredParams()
  const addToast = useToast()

  const [{ errorMessage, success }, { apiFunc: resendEmailFunc }] = useMutation(
    agentVerificationService.resendAgentVerificationEmail
  )

  useEffect(() => {
    if (success) {
      addToast(t('typography.agentVerificationEmailSent'), 'success')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success])

  const isPending =
    agentVerificationStatus &&
    [AgentVerificationStatus.PENDING_USER_VERIFICATION, AgentVerificationStatus.PENDING].includes(
      agentVerificationStatus
    )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <CardDashboard title={t('typography.agent')}>
      {isAgentEmailAutoApproved && (
        <>
          <Alert title={t('typography.applicantEmailAutoApproved')} variant="info" />
          <Spacer size={4} />
        </>
      )}

      <CardContent title={t('typography.name')}>
        <span>{`${agentVerification?.firstName} ${agentVerification?.lastName}`}</span>
      </CardContent>
      <CardContent title={t('typography.email')}>
        <div className={styles.emailWrapper}>
          <span>{agentVerification?.email || t('typography.notAvailable')}</span>
          {isPending && isOrgDataAvailable && hasInboxVerificationEmailSentLog && (
            <ButtonText onClick={() => resendEmailFunc({ id })} icon="recurring">
              {t('typography.resendAgentVerificationEmail')}
            </ButtonText>
          )}
        </div>
      </CardContent>
      <CardContent title={t('typography.language')}>
        <span>{`${agentVerification?.language}`}</span>
      </CardContent>
    </CardDashboard>
  )
}
